import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ProspectInfoModal({
  open,
  onOpenChange,
  title = "Prospect Info",
  defaultProspectNames = [""],
  defaultLeadId = null,
  defaultLeadLabel = "",
  requireLead = true,
  lockedProspects = null,
  onSubmit,
}) {
  // Prospect 1 doubles as Lead search/selection
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLeadLabel, setSelectedLeadLabel] = useState("");

  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  const [prospectDrafts, setProspectDrafts] = useState([""]);
  const [skippedLocked, setSkippedLocked] = useState({});

  const normalizedLocked = useMemo(() => {
    if (!Array.isArray(lockedProspects) || !lockedProspects.length) return [];
    return lockedProspects
      .map((p) => ({
        id: p?.id || null,
        name: String(p?.name || "").trim() || "Prospect",
      }))
      .filter((p) => p.name);
  }, [lockedProspects]);

  useEffect(() => {
    if (!open) return;

    const names =
      Array.isArray(defaultProspectNames) && defaultProspectNames.length
        ? defaultProspectNames
        : [""];

    // If a lead label is provided but Prospect 1 is empty, use it as the initial value.
    const next = [...names];
    if ((!next[0] || !String(next[0]).trim()) && defaultLeadLabel) {
      next[0] = defaultLeadLabel;
    }

    setProspectDrafts(next);
    setSelectedLeadId(defaultLeadId || null);
    setSelectedLeadLabel(defaultLeadLabel || "");
    setResults([]);
    setSkippedLocked({});
  }, [open, defaultProspectNames, defaultLeadId, defaultLeadLabel]);

  const primaryQuery = String(prospectDrafts?.[0] || "").trim();

  useEffect(() => {
    if (!open) return;

    // Once a lead is selected, hide dropdown and stop searching.
    if (selectedLeadId) {
      setResults([]);
      return;
    }

    const q = primaryQuery;
    if (q.length < 2) {
      setResults([]);
      return;
    }

    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get("/leads/search", { params: { q, limit: 10 } });
        setResults(res.data?.items || []);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [primaryQuery, open]);

  const showMatches = Boolean(
    open &&
      !selectedLeadId &&
      primaryQuery.length >= 2 &&
      (searching || results.length > 0)
  );

  const canSubmit = useMemo(() => {
    const names = (prospectDrafts || [])
      .map((n) => String(n || "").trim())
      .filter(Boolean);

    if (names.length === 0) return false;

    if (!requireLead) return true;

    // Lead is inferred from Prospect 1:
    // - either select an existing lead OR create a new lead with Prospect 1 name.
    if (selectedLeadId) return true;
    return String(prospectDrafts?.[0] || "").trim().length > 0;
  }, [prospectDrafts, requireLead, selectedLeadId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass border-white/10 max-w-2xl"
        data-testid="prospect-info-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-100">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-200/70">Prospects</div>

            <div className="space-y-2">
              {/* Locked prospects (existing appointment prospects): cannot edit or delete, only skip */}
              {normalizedLocked.map((p, idx) => {
                const isSkipped = Boolean(skippedLocked[p.id || p.name]);
                return (
                  <div key={p.id || p.name || idx} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={p.name}
                        disabled
                        placeholder={`Prospect ${idx + 1} name`}
                        className="bg-white/5 text-slate-200/80 placeholder:text-slate-400/70 ring-1 ring-white/10 opacity-80"
                        data-testid={`prospect-info-locked-name-${idx}`}
                      />

                      <Button
                        type="button"
                        variant="secondary"
                        className="rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15"
                        onClick={() =>
                          setSkippedLocked((prev) => ({
                            ...prev,
                            [p.id || p.name]: !Boolean(prev[p.id || p.name]),
                          }))
                        }
                        data-testid={`prospect-info-skip-${idx}`}
                      >
                        {isSkipped ? "Unskip" : "Skip Run"}
                      </Button>
                    </div>
                  </div>
                );
              })}

              {/* Editable (new) prospects for this run */}
              {prospectDrafts.map((name, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      value={name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setProspectDrafts((prev) => prev.map((p, i) => (i === idx ? v : p)));

                        // Prospect 1 controls lead selection.
                        if (idx === 0) {
                          setSelectedLeadId(null);
                          setSelectedLeadLabel("");
                        }
                      }}
                      placeholder={
                        idx === 0
                          ? "Prospect 1 (search existing lead or type to create)"
                          : `Prospect ${idx + 1} name`
                      }
                      className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                      data-testid={`prospect-info-name-${idx}`}
                    />

                    <Button
                      type="button"
                      variant="secondary"
                      className="rounded-full bg-white/10 text-slate-100 ring-1 ring-white/10 hover:bg-white/15 disabled:opacity-50"
                      onClick={() => setProspectDrafts((prev) => prev.filter((_, i) => i !== idx))}
                      disabled={prospectDrafts.length <= 1}
                      data-testid={`prospect-info-remove-${idx}`}
                    >
                      Remove
                    </Button>
                  </div>

                  {idx === 0 && showMatches ? (
                    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
                      <div className="border-b border-white/10 px-4 py-2 text-xs font-semibold text-slate-200/70">
                        Matches
                      </div>
                      <div className="max-h-[200px] overflow-auto">
                        {searching ? (
                          <div className="px-4 py-3 text-sm text-slate-200/70">Searching…</div>
                        ) : null}
                        {!searching && results.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-slate-200/70">No matches.</div>
                        ) : null}
                        {results.map((r) => {
                          const active = r.id === selectedLeadId;
                          const label = r.label || r.full_name || "Lead";
                          // Populate the input with ONLY the lead name (no phone), even if label is "Name — Phone".
                          const nameOnly = String(label || "")
                            .split(/\s*[—–-]\s*/)[0]
                            .trim();
                          return (
                            <button
                              key={r.id}
                              type="button"
                              className={
                                "w-full px-4 py-3 text-left text-sm hover:bg-white/10 " +
                                (active ? "bg-indigo-500/20" : "")
                              }
                              onClick={() => {
                                setSelectedLeadId(r.id);
                                setSelectedLeadLabel(nameOnly || label);
                                setResults([]);
                                setProspectDrafts((prev) => {
                                  const next = [...prev];
                                  next[0] = nameOnly || "";
                                  return next;
                                });
                              }}
                              data-testid={`prospect-info-lead-result-${r.id}`}
                            >
                              <div className="text-slate-100">{label}</div>
                              <div className="text-xs text-slate-200/60">{r.id}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {idx === 0 && requireLead ? (
                    !selectedLeadId && primaryQuery ? (
                      <div className="text-xs text-slate-200/70" data-testid="prospect-info-create-hint">
                        No lead selected — we’ll create a new lead named:{" "}
                        <span className="text-slate-100">{primaryQuery}</span>
                      </div>
                    ) : selectedLeadId ? (
                      <div className="text-xs text-slate-200/70" data-testid="prospect-info-selected-lead">
                        Selected lead: <span className="text-slate-100">{selectedLeadLabel || selectedLeadId}</span>
                      </div>
                    ) : null
                  ) : null}
                </div>
              ))}
            </div>

            <Button
              type="button"
              onClick={() => setProspectDrafts((prev) => [...prev, ""])}
              data-testid="prospect-info-add"
            >
              + Prospect
            </Button>

            {normalizedLocked.length ? (
              <div className="text-xs text-slate-200/60" data-testid="prospect-info-locked-hint">
                Existing appointment prospects are locked (append-only). Use “Skip Run” if someone isn’t present.
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange?.(false)}
            data-testid="prospect-info-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={async () => {
              const baseNames = (prospectDrafts || [])
                .map((n) => String(n || "").trim())
                .filter(Boolean);

              const lockedNames = normalizedLocked
                .filter((p) => !skippedLocked[p.id || p.name])
                .map((p) => p.name)
                .filter(Boolean);

              const names = [...lockedNames, ...baseNames];

              // Keep the same callback contract used elsewhere:
              // - lead_name is derived from Prospect 1
              await onSubmit?.({
                selected_lead_id: selectedLeadId,
                lead_name: names[0] || "",
                prospect_names: names,
              });
              onOpenChange?.(false);
            }}
            data-testid="prospect-info-submit"
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
